<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateDunningLetter',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    items: {
      type: Array,
      required: true,
      default: null,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const numbering = ref(null);
  const startingReferenceNumber = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    numbering: numbering.value,
    startingReferenceNumber: startingReferenceNumber.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_numbering = async () => {
    return $fn.value.fetch('read_numbering', {
      parameters: { id: 'sys.dunning_letter' },
    });
  };
  const $$load_startingReferenceNumber = async () => {
    return numbering.value.info.next_value;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'numbering' },
      { name: 'startingReferenceNumber', dependsOn: ['numbering'] },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      numbering: $$load_numbering,
      startingReferenceNumber: $$load_startingReferenceNumber,
    },
    ux: {},
    dataRefs: {
      numbering,
      startingReferenceNumber,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Create dunning letters') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_xg2lgh211_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_register_h650rb5q5_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'resultLength',
      value: $event.data.response.data ? $event.data.response.data.length : 0,
    };
  };
  const act_1040167445267876_notification_vqjtd4c59_getActionArgs = async (
    $event
  ) => {
    return {
      message: $fn.value.ngettext(
        'One dunning letter created.',
        '${n} dunning letters created.',
        $event.data.resultLength
      ),
    };
  };
  const act_1040167445267876_notification_vqjtd4c59_getWhen = async (
    $event
  ) => {
    return $event.data.resultLength > 0;
  };
  const act_1040167445267876_closeModal_4bv07g0lt_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_reloadSlickgrid_temb3k1c9_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_1040167445267876_request_udsupn316_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_dunning_letter',
      data: _.chain(props.items)
        .groupBy((p) => `${p.issuer.id}${p.client_id}`)
        .map((items, g) => ({
          company: {
            id: items[0].issuer.id,
          },
          client: {
            id: items[0].client_id,
          },
          items: _.map(items, (p) => ({ invoice_number: p.invoice_number })),
        }))
        .value(),
      parameters: {
        'starting-reference-number': startingReferenceNumber.value,
      },
    };
  };

  const act_7315092382398562_closeModal_xg2lgh211 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_xg2lgh211_getActionArgs,
    });
  const inst_evh_7315092382398562_close_z4q2ux9w9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_xg2lgh211,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_z4q2ux9w9],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_dwkyfes9q =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_4gddtuiad =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_dwkyfes9q,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_4gddtuiad],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_pfed7ihhc =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_fhefzfnl1 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_pfed7ihhc,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_fhefzfnl1],
    event: 'close',
  });
  const act_1040167445267876_register_h650rb5q5 =
    new actionTypes.RegisterAction({
      actionArgs: act_1040167445267876_register_h650rb5q5_getActionArgs,
    });
  const inst_evh_1040167445267876_success_twx8te4xz =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_register_h650rb5q5,
      event: 'success',
      displayName: 'register',
    });
  const act_1040167445267876_notification_vqjtd4c59 =
    new actionTypes.NotificationAction({
      actionArgs: act_1040167445267876_notification_vqjtd4c59_getActionArgs,
      when: act_1040167445267876_notification_vqjtd4c59_getWhen,
    });
  const inst_evh_1040167445267876_success_t4mrkmonz =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_notification_vqjtd4c59,
      event: 'success',
      displayName: 'notification',
    });
  const act_1040167445267876_closeModal_4bv07g0lt =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_4bv07g0lt_getActionArgs,
    });
  const inst_evh_1040167445267876_success_3j7ulnvou =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_4bv07g0lt,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_reloadSlickgrid_temb3k1c9 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_temb3k1c9_getActionArgs,
    });
  const inst_evh_1040167445267876_success_n34j7hzdr =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_temb3k1c9,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_request_udsupn316 = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_udsupn316_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_success_twx8te4xz,
      inst_evh_1040167445267876_success_t4mrkmonz,
      inst_evh_1040167445267876_success_3j7ulnvou,
      inst_evh_1040167445267876_success_n34j7hzdr,
    ],
  });
  const inst_evh_1040167445267876_submit_7rq3v8qnj =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_udsupn316,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_7rq3v8qnj],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_v7wkkk85s =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_o96ebgo53 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_v7wkkk85s,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_o96ebgo53],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_z4q2ux9w9,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_4gddtuiad,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_fhefzfnl1,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_twx8te4xz,
    inst_evh_1040167445267876_success_t4mrkmonz,
    inst_evh_1040167445267876_success_3j7ulnvou,
    inst_evh_1040167445267876_success_n34j7hzdr,
    inst_evh_1040167445267876_submit_7rq3v8qnj,
    submit: inst_evh_1040167445267876_submit_7rq3v8qnj,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_o96ebgo53,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_z4q2ux9w9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_z4q2ux9w9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_4gddtuiad = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_4gddtuiad.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_fhefzfnl1 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_fhefzfnl1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_twx8te4xz = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_twx8te4xz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_t4mrkmonz = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_t4mrkmonz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_3j7ulnvou = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_3j7ulnvou.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_n34j7hzdr = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_n34j7hzdr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_7rq3v8qnj = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_7rq3v8qnj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_o96ebgo53 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_o96ebgo53.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateDunningLetter"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="create-options"
        id="om71edxw7f"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="lastNumber"
          :label="gettext('Last reference number')"
          :label-cols-md="5"
          :content-cols-md="7"
        >
          <BFormInput
            plaintext
            v-model="numbering.info.last_number"
            :value="numbering.info.last_number"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (numbering.info.last_number =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            class="fw-bold no-focus-border"
          >
          </BFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          row
          name="startingReferenceNumber"
          :label="gettext('Next reference number')"
          :label-cols-md="5"
          :content-cols-md="3"
        >
          <ItpFormInput
            type="number"
            name="startingReferenceNumber"
            size="md"
            v-model="startingReferenceNumber"
            :value="startingReferenceNumber"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (startingReferenceNumber =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            number
            :min="numbering.info.next_value"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpAlert show>
          <ItpIcon name="fa-info-circle" class="me-1"> </ItpIcon>
          <span>
            {{
              gettext(
                'Next reference numbers will be generated from the given value. It must be higher than the current value.'
              )
            }}
          </span>
        </ItpAlert>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          form="om71edxw7f"
          :text="pgettext('DunningLetterCreatePage', 'Generate')"
          type="submit"
          variant="primary"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
